import React from "react";
import { useNode } from "@craftjs/core";
import {
    Box,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Slider,
    Grid,
    Typography,
    Divider,
    TextField,
    Paper
} from "@material-ui/core";

export const Flexbox = ({
                            // Flex container properties
                            flexDirection,
                            justifyContent,
                            alignItems,
                            flexWrap,
                            gap,

                            // Layout properties
                            padding,
                            margin,
                            width,
                            minHeight,
                            backgroundColor,
                            borderRadius,

                            // Child elements
                            children
                        }) => {
    const {
        connectors: { connect, drag },
        selected,
        actions: { setProp }
    } = useNode((node) => ({
        selected: node.events.selected
    }));

    return (
        <Box>
            <div
                ref={ref => connect(drag(ref))}
                style={{
                    // Flex properties
                    display: 'flex',
                    flexDirection,
                    justifyContent,
                    alignItems,
                    flexWrap,
                    gap: `${gap}px`,

                    // Layout properties
                    padding: `${padding}px`,
                    margin: `${margin}px`,
                    width: width === 'custom' ? `${width}px` : width,
                    minHeight: `${minHeight}px`,
                    backgroundColor,
                    borderRadius: `${borderRadius}px`,

                    // Visual feedback
                    border: selected ? '1px dashed #000' : '1px solid rgba(0,0,0,0.1)',
                    transition: 'all 0.2s',
                }}
            >
                {children}
            </div>

            {/* Settings Panel */}
            {selected && (
                <Box sx={{ mt: 2 }}>
                    <Paper>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Flexbox Settings
                            </Typography>

                            <Grid container spacing={2}>
                                {/* Basic Flex Properties */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Direction</FormLabel>
                                        <Select
                                            value={flexDirection}
                                            onChange={(e) => setProp(props => props.flexDirection = e.target.value)}
                                        >
                                            <MenuItem value="row">Row</MenuItem>
                                            <MenuItem value="column">Column</MenuItem>
                                            <MenuItem value="row-reverse">Row Reverse</MenuItem>
                                            <MenuItem value="column-reverse">Column Reverse</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Wrap</FormLabel>
                                        <Select
                                            value={flexWrap}
                                            onChange={(e) => setProp(props => props.flexWrap = e.target.value)}
                                        >
                                            <MenuItem value="nowrap">No Wrap</MenuItem>
                                            <MenuItem value="wrap">Wrap</MenuItem>
                                            <MenuItem value="wrap-reverse">Wrap Reverse</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Alignment Properties */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Justify Content</FormLabel>
                                        <Select
                                            value={justifyContent}
                                            onChange={(e) => setProp(props => props.justifyContent = e.target.value)}
                                        >
                                            <MenuItem value="flex-start">Start</MenuItem>
                                            <MenuItem value="center">Center</MenuItem>
                                            <MenuItem value="flex-end">End</MenuItem>
                                            <MenuItem value="space-between">Space Between</MenuItem>
                                            <MenuItem value="space-around">Space Around</MenuItem>
                                            <MenuItem value="space-evenly">Space Evenly</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Align Items</FormLabel>
                                        <Select
                                            value={alignItems}
                                            onChange={(e) => setProp(props => props.alignItems = e.target.value)}
                                        >
                                            <MenuItem value="flex-start">Start</MenuItem>
                                            <MenuItem value="center">Center</MenuItem>
                                            <MenuItem value="flex-end">End</MenuItem>
                                            <MenuItem value="stretch">Stretch</MenuItem>
                                            <MenuItem value="baseline">Baseline</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                </Grid>

                                {/* Spacing Controls */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>Gap (px)</FormLabel>
                                        <Slider
                                            value={gap}
                                            onChange={(_, value) => setProp(props => props.gap = value)}
                                            min={0}
                                            max={40}
                                            step={4}
                                            marks={[
                                                { value: 0, label: '0' },
                                                { value: 20, label: '20' },
                                                { value: 40, label: '40' }
                                            ]}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>Padding (px)</FormLabel>
                                        <Slider
                                            value={padding}
                                            onChange={(_, value) => setProp(props => props.padding = value)}
                                            min={0}
                                            max={40}
                                            step={4}
                                            marks={[
                                                { value: 0, label: '0' },
                                                { value: 20, label: '20' },
                                                { value: 40, label: '40' }
                                            ]}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Size Controls */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Width</FormLabel>
                                        <Select
                                            value={width}
                                            onChange={(e) => setProp(props => props.width = e.target.value)}
                                        >
                                            <MenuItem value="100%">Full Width</MenuItem>
                                            <MenuItem value="75%">75%</MenuItem>
                                            <MenuItem value="50%">50%</MenuItem>
                                            <MenuItem value="25%">25%</MenuItem>
                                            <MenuItem value="custom">Custom</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Min Height (px)</FormLabel>
                                        <TextField
                                            type="number"
                                            value={minHeight}
                                            onChange={(e) => setProp(props => props.minHeight = parseInt(e.target.value))}
                                            inputProps={{ min: 0 }}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Style Controls */}
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Border Radius (px)</FormLabel>
                                        <Slider
                                            value={borderRadius}
                                            onChange={(_, value) => setProp(props => props.borderRadius = value)}
                                            min={0}
                                            max={20}
                                            step={2}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <FormLabel>Margin (px)</FormLabel>
                                        <Slider
                                            value={margin}
                                            onChange={(_, value) => setProp(props => props.margin = value)}
                                            min={0}
                                            max={40}
                                            step={4}
                                            marks={[
                                                { value: 0, label: '0' },
                                                { value: 20, label: '20' },
                                                { value: 40, label: '40' }
                                            ]}
                                        />
                                    </FormControl>
                                </Grid>

                                {/* Background Color */}
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <FormLabel>Background Color</FormLabel>
                                        <TextField
                                            value={backgroundColor}
                                            onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
                                            placeholder="e.g. #f0f0f0 or transparent"
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

// Component default props and rules
Flexbox.craft = {
    props: {
        // Default flex properties
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        flexWrap: 'nowrap',
        gap: 8,

        // Default layout properties
        padding: 16,
        margin: 0,
        width: '100%',
        minHeight: 100,
        backgroundColor: '#f5f5f5',
        borderRadius: 0
    }
};